import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { LogOut, User, Plane, LayoutDashboard, Table2, Settings, Clock, Users, Award, CheckSquare, Wrench, ChevronDown, CalendarDays, CalendarClock, Radio, Sun, Megaphone, FileSpreadsheet, Package, ClipboardList, Landmark, Truck, MonitorPlay, MessagesSquare, Boxes, PackageSearch, HelpCircle, FileOutput, Hammer, Gauge, ArrowLeftRight, ScanSearch, Globe, LifeBuoy } from "lucide-react";
import { canManageAircraftStatus, isLeader } from "@/lib/permissions";
import NavInstallButton from "@/components/NavInstallButton";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const linkClass = ({ isActive }) =>
  `px-3 py-1.5 rounded-md text-sm transition-colors ${
    isActive ? "bg-summit-primary text-white" : "text-summit-primary-soft hover:bg-summit-glacier"
  }`;

/** A dropdown "menu ▾" nav item. Highlights itself when the current URL
 * matches any of the child routes (mirrors NavLink's active state so leaders
 * can see which group they're inside). */
function GroupMenu({ label, icon: Icon, items, testId }) {
  const { pathname } = useLocation();
  const active = items.some((it) => pathname.startsWith(it.to));
  const badgeTotal = items.reduce((s, it) => s + (it.badge || 0), 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`px-3 py-1.5 rounded-md text-sm transition-colors inline-flex items-center gap-1.5 ${
            active ? "bg-summit-primary text-white" : "text-summit-primary-soft hover:bg-summit-glacier"
          }`}
          data-testid={testId}
        >
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label}
          {badgeTotal > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white font-mono text-[10px] inline-flex items-center justify-center" data-testid={`${testId}-badge`}>
              {badgeTotal > 99 ? "99+" : badgeTotal}
            </span>
          )}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
          {label}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((it) => (
          <DropdownMenuItem key={it.to} asChild>
            <NavLink
              to={it.to}
              className={({ isActive }) =>
                `flex items-center gap-2 text-sm ${isActive ? "text-summit-primary font-medium" : ""}`
              }
              data-testid={it.testId}
            >
              {it.icon && <it.icon className="w-3.5 h-3.5" />}
              <span>{it.label}</span>
              {(it.badge || 0) > 0 && (
                <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white font-mono text-[10px] inline-flex items-center justify-center" data-testid={`${it.testId}-badge`}>
                  {it.badge > 99 ? "99+" : it.badge}
                </span>
              )}
            </NavLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [taskBadge, setTaskBadge] = useState(0);
  const [discBadge, setDiscBadge] = useState(0);
  const [kpiBadge, setKpiBadge] = useState(0);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const fetchCount = () => api.get("/tasks/badge-count").then((r) => { if (alive) setTaskBadge(r.data?.count || 0); }).catch(() => {});
    const fetchDisc = () => api.get("/discussions/unread-count").then((r) => { if (alive) setDiscBadge(r.data?.count || 0); }).catch(() => {});
    const fetchKpi = () => api.get("/kpi/badge-count").then((r) => { if (alive) setKpiBadge(r.data?.count || 0); }).catch(() => {});
    fetchCount();
    fetchDisc();
    fetchKpi();
    const iv = setInterval(() => { fetchCount(); fetchDisc(); fetchKpi(); }, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [user, pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) return null;


  // Contents of the Team ▾ dropdown — assembled per-user so items only show
  // for roles that can actually reach the route. Discussion + News are for
  // everyone, so the menu now renders for all users.
  const teamItems = [];
  if (user.can_have_reports) {
    teamItems.push({ to: "/team", label: "Timesheets", icon: Users, testId: "nav-team" });
    teamItems.push({ to: "/team/on-duty", label: "On Duty Now", icon: Radio, testId: "nav-on-duty" });
    teamItems.push({ to: "/team/on-vacation", label: "On Vacation", icon: Sun, testId: "nav-on-vacation" });
  }
  if (isLeader(user)) {
    teamItems.push({ to: "/schedule", label: "Schedule", icon: CalendarDays, testId: "nav-schedule" });
    teamItems.push({ to: "/team/shift-log", label: "Shift Log", icon: CalendarClock, testId: "nav-shift-log" });
  }
  if (user.role === "admin") {
  }
  if (user.role === "crew_chief") {
  }
  // Timesheet template upload — Admins + Senior Managers.
  if (user.role === "admin" || user.job_title === "Senior Manager") {
    teamItems.push({ to: "/leadership/template", label: "Timesheet Template", icon: FileSpreadsheet, testId: "nav-template-admin" });
  }
  // Discussion + News live in the Team menu — visible to everyone.
  teamItems.push({ to: "/team/discussion", label: "Discussion", icon: MessagesSquare, testId: "nav-discussion", badge: discBadge });
  teamItems.push({ to: "/announcements", label: "Announcements", icon: Megaphone, testId: "nav-announcements" });
  if (user.kpi_access || user.role === "admin") {
    teamItems.push({ to: "/team/kpi", label: "KPI", icon: Gauge, testId: "nav-kpi", badge: kpiBadge });
  }

  // Stores ▾ dropdown — Repairs + PO Overview, each gated by its own access flag.
  const storesItems = [];
  if (user.repair_approvals_access || user.role === "admin") {
    storesItems.push({ to: "/repair-approvals", label: "Repairs", icon: Wrench, testId: "nav-repairs" });
  }
  if (user.po_overview_access || user.role === "admin") {
    storesItems.push({ to: "/po-overview", label: "PO Overview", icon: ClipboardList, testId: "nav-po-overview" });
  }
  if (user.parts_access || user.role === "admin") {
    storesItems.push({ to: "/stores/parts", label: "Parts Requests", icon: Package, testId: "nav-parts" });
  }
  if (user.critical_spares_access || user.role === "admin") {
    storesItems.push({ to: "/stores/critical-spares", label: "Critical Spares", icon: Boxes, testId: "nav-critical-spares" });
  }
  if (user.spares_monitoring_access || user.role === "admin") {
    storesItems.push({ to: "/stores/spares-monitoring", label: "Spares Monitoring", icon: PackageSearch, testId: "nav-spares-monitoring" });
  }
  if (user.pr_controls_access || user.role === "admin") {
    storesItems.push({ to: "/stores/pr-controls", label: "PR Controls", icon: ScanSearch, testId: "nav-pr-controls" });
  }
  if (user.search_engine_access || user.role === "admin") {
    storesItems.push({ to: "/stores/search-engine", label: "Search Engine", icon: Globe, testId: "nav-search-engine" });
  }
  const hadGatedStores = storesItems.length > 0;
  // Tool Status — camera tag scanner + sign-out tracker, visible to everyone.
  storesItems.push({ to: "/stores/tool-status", label: "Tool Status", icon: Hammer, testId: "nav-tool-status" });
  if (hadGatedStores) {
    storesItems.push({ to: "/stores/form-generator", label: "Form Generator", icon: FileOutput, testId: "nav-form-generator" });
  }

  // Fleet ▾ dropdown — Maintenance on top (everyone), Fleet Status for leaders.
  const fleetItems = [
    { to: "/fleet/maintenance", label: "Maintenance", icon: Wrench, testId: "nav-maintenance" },
  ];
  if (user.planning_access || user.role === "admin") {
    fleetItems.push({ to: "/fleet/planning", label: "Planning", icon: CalendarClock, testId: "nav-planning" });
  }
  if (user.handover_access || user.role === "admin") {
    fleetItems.push({ to: "/fleet/handovers", label: "Handovers", icon: ArrowLeftRight, testId: "nav-handovers" });
  }
  if (user.heavy_check_access || user.role === "admin") {
    fleetItems.push({ to: "/fleet/heavy-checks", label: "Heavy Check Status", icon: ClipboardList, testId: "nav-heavy-checks" });
  }
  if (user.ts_helper_access || user.role === "admin") {
    fleetItems.push({ to: "/fleet/ts-helper", label: "T/S Helper", icon: LifeBuoy, testId: "nav-ts-helper" });
  }
  if (canManageAircraftStatus(user)) {
    fleetItems.push({ to: "/fleet-status", label: "Fleet Status", icon: Plane, testId: "nav-fleet-status" });
  }
  fleetItems.push({ to: "/fleet/board", label: "Fleet Board (TV)", icon: MonitorPlay, testId: "nav-fleet-board" });

  return (
    <header className="border-b border-summit-hairline bg-white sticky top-0 z-30" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <img src="/media/sa-logo.png" alt="Summit Air" className="w-9 h-9 object-contain rounded-md" data-testid="brand-logo" />
            <div className="leading-tight">
              <div className="font-display font-semibold tracking-tight text-[15px]">SA</div>
              <div className="font-mono text-[10px] text-gray-500 uppercase">System · MST</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/clock" className={linkClass} data-testid="nav-clock">
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/>Clock In</span>
            </NavLink>
            {storesItems.length > 0 && (
              <GroupMenu
                label="Stores"
                icon={Package}
                items={storesItems}
                testId="nav-stores-menu"
              />
            )}
            <GroupMenu
              label="Fleet"
              icon={Plane}
              items={fleetItems}
              testId="nav-fleet-menu"
            />
            {(user.capital_access || user.role === "admin") && (
              <NavLink to="/capital" className={linkClass} data-testid="nav-capital">
                <span className="inline-flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5"/>Capital</span>
              </NavLink>
            )}
            {(user.gse_access || user.role === "admin") && (
              <NavLink to="/gse" className={linkClass} data-testid="nav-gse">
                <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5"/>GSE</span>
              </NavLink>
            )}
            {(user.mshop_access || user.role === "admin") && (
              <NavLink to="/m-shop" className={linkClass} data-testid="nav-mshop">
                <span className="inline-flex items-center gap-1.5"><Hammer className="w-3.5 h-3.5"/>M-Shop</span>
              </NavLink>
            )}
            {teamItems.length > 0 && (
              <GroupMenu
                label="Team"
                icon={Users}
                items={teamItems}
                testId="nav-team-menu"
              />
            )}
            {(user.role === "admin" || user.role === "crew_chief") && (
              <GroupMenu
                label="Admin"
                icon={Settings}
                items={[
                  { to: "/admin/settings", label: "Settings", icon: Settings, testId: "nav-admin-settings" },
                ]}
                testId="nav-admin-menu"
              />
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <NavInstallButton />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="hidden sm:flex items-center gap-1.5 text-right leading-tight px-2 py-1 rounded-md hover:bg-summit-glacier transition-colors"
                data-testid="nav-user-menu"
              >
                <div>
                  <div className="text-sm font-medium">{user.display_name}</div>
                </div>
                {(taskBadge + kpiBadge) > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white font-mono text-[10px] inline-flex items-center justify-center" data-testid="nav-user-menu-badge">
                    {(taskBadge + kpiBadge) > 99 ? "99+" : taskBadge + kpiBadge}
                  </span>
                )}
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild>
                <NavLink to="/profile" className="flex items-center gap-2 text-sm" data-testid="nav-profile">
                  <User className="w-3.5 h-3.5" /> Profile
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <NavLink to="/tasks" className="flex items-center gap-2 text-sm" data-testid="nav-tasks">
                  <CheckSquare className="w-3.5 h-3.5" /> My Tasks
                  {taskBadge > 0 && (
                    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white font-mono text-[10px] inline-flex items-center justify-center" data-testid="nav-tasks-badge">
                      {taskBadge > 99 ? "99+" : taskBadge}
                    </span>
                  )}
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <NavLink to="/timesheet" className="flex items-center gap-2 text-sm" data-testid="nav-timesheet">
                  <Table2 className="w-3.5 h-3.5" /> My Timesheet
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <NavLink to="/reviews" className="flex items-center gap-2 text-sm" data-testid="nav-reviews">
                  <Award className="w-3.5 h-3.5" /> Reviews
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <NavLink to="/faq" className="flex items-center gap-2 text-sm" data-testid="nav-faq">
                  <HelpCircle className="w-3.5 h-3.5" /> FAQ / Help
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-sm text-rose-600 focus:text-rose-600" data-testid="nav-logout-item">
                <LogOut className="w-3.5 h-3.5" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink
            to="/profile"
            className="md:hidden p-2 rounded-md hover:bg-summit-glacier text-gray-700"
            aria-label="Profile"
            data-testid="nav-profile-mobile"
          >
            <User className="w-4 h-4" />
          </NavLink>
          <button
            onClick={handleLogout}
            className="p-2 rounded-md hover:bg-summit-glacier text-gray-700"
            data-testid="logout-btn"
            aria-label="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
